import Model from "@tripian/model";
import moment from "moment";

/**
 * Adapts TimelineTrip to TripReference-like object
 * Used for compatibility with existing components that expect TripReference
 */
export const adaptTimelineTripToReference = (
  timelineTrip: Model.TimelineTrip
): Model.TripReference => {
  // Get first and last plan dates for arrival/departure
  const plans = timelineTrip.plans || [];
  const firstPlan = plans[0];
  const lastPlan = plans[plans.length - 1];

  // Build TripProfile from TimelineTripProfile
  const tripProfile: Model.TripProfile = {
    cityId: timelineTrip.tripProfile?.cityId || timelineTrip.city.id,
    arrivalDatetime: firstPlan?.startDate || moment().format("YYYY-MM-DD HH:mm"),
    departureDatetime: lastPlan?.endDate || moment().add(1, "day").format("YYYY-MM-DD HH:mm"),
    numberOfAdults: timelineTrip.tripProfile?.adults || firstPlan?.adults || 1,
    numberOfChildren: timelineTrip.tripProfile?.children || firstPlan?.children || 0,
    accommodation: null, // Timeline doesn't have global accommodation
    answers: timelineTrip.tripProfile?.answerIds || [],
    companionIds: [],
    excludeHash: timelineTrip.tripProfile?.excludeHashPois || [],
    doNotGenerate: 0,
    tripName: (timelineTrip.tripProfile as any)?.cityName || timelineTrip.city.name,
  };

  return {
    id: timelineTrip.id,
    tripHash: timelineTrip.tripHash,
    shared: timelineTrip.shared,
    city: timelineTrip.city,
    tripProfile,
  };
};

/**
 * Represents a segment within a day (booking or generated itinerary)
 */
export interface TimelineSegment {
  id: string;
  name: string;
  description: string;
  available: boolean;
  startTime: string;
  endTime: string;
  steps: Model.Step[];
  generatedStatus: number;
  cityName: string;
}

/**
 * Adapts TimelinePlan to Plan-like object
 * Used for compatibility with existing components that expect Plan
 */
export const adaptTimelinePlanToPlan = (
  timelinePlan: Model.TimelinePlan,
  index: number
): Model.Plan => {
  // Extract date from startDate (format: "YYYY-MM-DD HH:mm")
  const startMoment = moment(timelinePlan.startDate);
  const endMoment = moment(timelinePlan.endDate);

  // TimelinePlan.id is a string (e.g., "25059"), convert to number
  const numericId = parseInt(timelinePlan.id, 10);

  // Convert startDateTimes/endDateTimes to times format
  const stepsWithTimes = (timelinePlan.steps || []).map(step => {
    let times = step.times;
    if (!times || (!times.from && !times.to)) {
      const stepAny = step as any;
      const fromTime = stepAny.startDateTimes
        ? moment(stepAny.startDateTimes).format("HH:mm")
        : "";
      const toTime = stepAny.endDateTimes
        ? moment(stepAny.endDateTimes).format("HH:mm")
        : "";
      times = { from: fromTime, to: toTime };
    }
    return {
      ...step,
      times,
    };
  });

  return {
    id: isNaN(numericId) ? index + 1 : numericId,
    date: startMoment.format("YYYY-MM-DD"),
    startTime: startMoment.format("HH:mm"),
    endTime: endMoment.format("HH:mm"),
    generatedStatus: timelinePlan.generatedStatus,
    steps: stepsWithTimes,
  };
};

/**
 * Extended TimelinePlan with additional display info
 */
export interface AdaptedTimelinePlan extends Model.Plan {
  timelinePlanId: string;
  available: boolean;
  name: string;
  description: string;
  cityName: string;
}

/**
 * Represents a day with multiple segments (bookings + itineraries)
 */
export interface TimelineDay {
  dayIndex: number;
  date: string;
  segments: TimelineSegment[];
  // Combined steps from all available segments for map/itinerary display
  allSteps: Model.Step[];
  // Plan-compatible object for existing components
  plan: Model.Plan;
}

/**
 * Splits a plan that spans multiple days into separate day entries
 * Plan ID format "25063-25064" means day1=25063, day2=25064
 */
interface SplitPlanEntry {
  plan: Model.TimelinePlan;
  dayId: string;
  date: string;
}

const splitPlanByDates = (plan: Model.TimelinePlan): SplitPlanEntry[] => {
  const startDate = moment(plan.startDate);
  const endDate = moment(plan.endDate);
  const startDateStr = startDate.format("YYYY-MM-DD");
  const endDateStr = endDate.format("YYYY-MM-DD");

  // Check if plan spans multiple days
  if (startDateStr === endDateStr) {
    // Single day plan
    return [{ plan, dayId: plan.id, date: startDateStr }];
  }

  // Multi-day plan - split by dates
  const ids = plan.id.split("-");
  const entries: SplitPlanEntry[] = [];

  // Calculate number of days
  const daysDiff = endDate.diff(startDate, "days") + 1;

  for (let i = 0; i < daysDiff; i++) {
    const currentDate = moment(startDate).add(i, "days").format("YYYY-MM-DD");
    const dayId = ids[i] || ids[ids.length - 1]; // Use corresponding ID or last one

    entries.push({
      plan,
      dayId,
      date: currentDate,
    });
  }

  return entries;
};

/**
 * Groups TimelinePlans by date to create TimelineDays
 * Multiple segments on the same date become one "day"
 * Handles multi-day plans with IDs like "25063-25064"
 */
export const groupTimelinePlansByDate = (
  timelinePlans: Model.TimelinePlan[]
): TimelineDay[] => {
  if (!timelinePlans || timelinePlans.length === 0) return [];

  // Split multi-day plans and group by date
  const dateGroupMap = new Map<string, { plan: Model.TimelinePlan; dayId: string }[]>();

  timelinePlans.forEach(plan => {
    const splitEntries = splitPlanByDates(plan);
    splitEntries.forEach(entry => {
      const existing = dateGroupMap.get(entry.date) || [];
      existing.push({ plan: entry.plan, dayId: entry.dayId });
      dateGroupMap.set(entry.date, existing);
    });
  });

  // Convert to TimelineDay array, sorted by date
  const sortedDates = Array.from(dateGroupMap.keys()).sort();

  return sortedDates.map((date, dayIndex) => {
    const entriesForDate = dateGroupMap.get(date) || [];

    // Create segments from plans
    const segments: TimelineSegment[] = entriesForDate.map(entry => ({
      id: entry.dayId,
      name: entry.plan.name,
      description: entry.plan.description,
      available: entry.plan.available,
      startTime: moment(entry.plan.startDate).format("HH:mm"),
      endTime: moment(entry.plan.endDate).format("HH:mm"),
      steps: entry.plan.steps || [],
      generatedStatus: entry.plan.generatedStatus,
      cityName: entry.plan.city?.name || "",
    }));

    // Combine all steps from all segments (both available and booking)
    // Filter steps that belong to this specific date
    const allSteps: Model.Step[] = [];
    entriesForDate.forEach(entry => {
      if (entry.plan.steps && entry.plan.steps.length > 0) {
        // Convert startDateTimes/endDateTimes to times format
        // Only include steps that match this date
        const stepsWithTimes = entry.plan.steps
          .filter(step => {
            const stepAny = step as any;
            if (stepAny.startDateTimes) {
              const stepDate = moment(stepAny.startDateTimes).format("YYYY-MM-DD");
              return stepDate === date;
            }
            return true; // Include if no date info
          })
          .map(step => {
            let times = step.times;
            if (!times || (!times.from && !times.to)) {
              const stepAny = step as any;
              const fromTime = stepAny.startDateTimes
                ? moment(stepAny.startDateTimes).format("HH:mm")
                : "";
              const toTime = stepAny.endDateTimes
                ? moment(stepAny.endDateTimes).format("HH:mm")
                : "";
              times = { from: fromTime, to: toTime };
            }
            return {
              ...step,
              times,
            };
          });
        allSteps.push(...stepsWithTimes);
      }
    });

    // Get first and last times for the day
    const firstEntry = entriesForDate[0];
    const lastEntry = entriesForDate[entriesForDate.length - 1];
    const startTime = moment(firstEntry.plan.startDate).format("HH:mm");
    const endTime = moment(lastEntry.plan.endDate).format("HH:mm");

    // Create Plan-compatible object using first available entry's dayId
    const availableEntry = entriesForDate.find(e => e.plan.available) || firstEntry;
    const planId = parseInt(availableEntry.dayId, 10);

    // Use generatedStatus from available entry, but if we have steps, ensure it's not 0
    // This handles the case where a booking segment (generatedStatus=0) is combined with
    // a generated segment that has steps
    let generatedStatus = availableEntry.plan.generatedStatus;
    if (allSteps.length > 0 && generatedStatus === 0) {
      // If we have steps but generatedStatus is 0, find an entry with steps and use its status
      const entryWithSteps = entriesForDate.find(e => e.plan.steps && e.plan.steps.length > 0);
      if (entryWithSteps) {
        generatedStatus = entryWithSteps.plan.generatedStatus;
      }
    }

    const plan: Model.Plan = {
      id: isNaN(planId) ? dayIndex + 1 : planId,
      date,
      startTime,
      endTime,
      generatedStatus,
      steps: allSteps,
    };

    return {
      dayIndex,
      date,
      segments,
      allSteps,
      plan,
    };
  });
};

/**
 * Get plans array for Redux compatibility (one plan per day)
 */
export const getPlansFromTimelineDays = (days: TimelineDay[]): Model.Plan[] => {
  return days.map(day => day.plan);
};

/**
 * Adapts array of TimelinePlans to array of Plans
 */
export const adaptTimelinePlansToPlan = (
  timelinePlans: Model.TimelinePlan[]
): Model.Plan[] => {
  return timelinePlans.map((plan, index) => adaptTimelinePlanToPlan(plan, index));
};

/**
 * Gets the correct plan index for a given day index
 * TimelinePlans might have different structure than regular Plans
 */
export const getTimelinePlanByDayIndex = (
  plans: Model.TimelinePlan[],
  dayIndex: number
): Model.TimelinePlan | undefined => {
  if (!plans || plans.length === 0) return undefined;
  if (dayIndex < 0 || dayIndex >= plans.length) return plans[0];
  return plans[dayIndex];
};

/**
 * Extended TimelinePlan info for display purposes
 */
export interface TimelinePlanDisplayInfo {
  planId: string;
  dayIndex: number;
  name: string;
  description: string;
  isAvailable: boolean;
  cityName: string;
  date: string;
  startTime: string;
  endTime: string;
  stepsCount: number;
}

/**
 * Get display info for a TimelinePlan
 */
export const getTimelinePlanDisplayInfo = (
  plan: Model.TimelinePlan,
  dayIndex: number
): TimelinePlanDisplayInfo => {
  const startMoment = moment(plan.startDate);
  const endMoment = moment(plan.endDate);

  return {
    planId: plan.id,
    dayIndex,
    name: plan.name,
    description: plan.description,
    isAvailable: plan.available,
    cityName: plan.city?.name || "",
    date: startMoment.format("YYYY-MM-DD"),
    startTime: startMoment.format("HH:mm"),
    endTime: endMoment.format("HH:mm"),
    stepsCount: plan.steps?.length || 0,
  };
};

/**
 * Segment-based grouping data structure
 * Groups all plan data under each segment from tripProfile.segments
 */
export interface TimelineSegmentGroup {
  segmentIndex: number;
  title: string;
  description: string;
  available: boolean;
  smartRecommendation: boolean;
  startDate: string;
  endDate: string;
  cityName: string;
  // All steps belonging to this segment (not split by date)
  steps: Model.Step[];
  // The TimelinePlan associated with this segment
  plan: Model.TimelinePlan | null;
  // Color for map routes (assigned based on segment index)
  routeColor: string;
}

// Predefined colors for segment routes
const SEGMENT_ROUTE_COLORS = [
  "#4285F4", // Blue
];

/**
 * Helper to convert step times from startDateTimes/endDateTimes format
 */
const convertStepTimes = (step: Model.Step): Model.Step => {
  let times = step.times;
  if (!times || (!times.from && !times.to)) {
    const stepAny = step as any;
    const fromTime = stepAny.startDateTimes
      ? moment(stepAny.startDateTimes).format("HH:mm")
      : "";
    const toTime = stepAny.endDateTimes
      ? moment(stepAny.endDateTimes).format("HH:mm")
      : "";
    times = { from: fromTime, to: toTime };
  }
  return { ...step, times };
};

/**
 * Find matching plans for a segment using dayIds
 * dayIds contains the plan IDs that belong to this segment
 */
const findMatchingPlans = (
  segment: Model.TimelineTripProfileSegment,
  timelinePlans: Model.TimelinePlan[],
  fallbackIndex: number
): Model.TimelinePlan[] => {
  const segmentAny = segment as any;

  // Use dayIds if available for ID-based matching
  if (segmentAny.dayIds && Array.isArray(segmentAny.dayIds) && segmentAny.dayIds.length > 0) {
    const matchingPlans = timelinePlans.filter(plan => {
      const planId = parseInt(plan.id, 10);
      return segmentAny.dayIds.includes(planId);
    });
    if (matchingPlans.length > 0) {
      return matchingPlans;
    }
  }

  // Fallback to index-based matching
  const fallbackPlan = timelinePlans[fallbackIndex];
  return fallbackPlan ? [fallbackPlan] : [];
};

/**
 * Groups TimelinePlans by tripProfile.segments
 * Each segment becomes a collapse header with all its plan data inside
 * Uses dayIds for reliable plan matching (falls back to index if not available)
 */
export const groupTimelinePlansBySegment = (
  timelinePlans: Model.TimelinePlan[],
  tripProfileSegments?: Model.TimelineTripProfileSegment[]
): TimelineSegmentGroup[] => {
  if (!tripProfileSegments || tripProfileSegments.length === 0) {
    // Fallback: use plans directly if no segments defined
    return timelinePlans.map((plan, index) => {
      const stepsWithTimes = (plan.steps || []).map(convertStepTimes);

      return {
        segmentIndex: index,
        title: plan.name || `Segment ${index + 1}`,
        description: plan.description || "",
        available: plan.available,
        smartRecommendation: true, // Default to true for fallback
        startDate: plan.startDate,
        endDate: plan.endDate,
        cityName: plan.city?.name || "",
        steps: stepsWithTimes,
        plan,
        routeColor: SEGMENT_ROUTE_COLORS[index % SEGMENT_ROUTE_COLORS.length],
      };
    });
  }

  // Match each tripProfile segment with its corresponding plan(s) using dayIds
  return tripProfileSegments.map((segment, index) => {
    // Find matching plans using dayIds (or fallback to index)
    const matchingPlans = findMatchingPlans(segment, timelinePlans, index);

    // Use first matching plan as the primary plan
    const primaryPlan = matchingPlans[0] || null;

    // Combine steps from all matching plans
    const allSteps: Model.Step[] = matchingPlans.flatMap(plan =>
      (plan.steps || []).map(convertStepTimes)
    );

    // Get smartRecommendation from segment (default to true if not specified)
    const segmentAny = segment as any;
    const smartRecommendation = segmentAny.smartRecommendation !== false;

    return {
      segmentIndex: index,
      title: segment.title || primaryPlan?.name || `Segment ${index + 1}`,
      description: segment.description || primaryPlan?.description || "",
      available: segment.available,
      smartRecommendation,
      startDate: segment.startDate,
      endDate: segment.endDate,
      cityName: primaryPlan?.city?.name || "",
      steps: allSteps,
      plan: primaryPlan,
      routeColor: SEGMENT_ROUTE_COLORS[index % SEGMENT_ROUTE_COLORS.length],
    };
  });
};

/**
 * Get all steps from all segments for map display
 * Returns steps grouped by segment with route colors
 */
export interface SegmentStepsForMap {
  segmentIndex: number;
  steps: Model.Step[];
  routeColor: string;
  available: boolean;
}

export const getSegmentStepsForMap = (
  segmentGroups: TimelineSegmentGroup[]
): SegmentStepsForMap[] => {
  return segmentGroups
    .filter(group => (group.available || group.smartRecommendation) && group.steps.length > 0)
    .map(group => ({
      segmentIndex: group.segmentIndex,
      steps: group.steps,
      routeColor: group.routeColor,
      available: group.available,
    }));
};
